import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ToastNotification {
    id: string;
    lifetimeKey?: string;
    title?: string | null;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    timeout?: number | null;
    loading?: boolean;
    action?: {
        label: string;
        href: string;
        /** Opt-in: the toast closes when the visit of its action starts, then this runs. */
        onVisit?: () => void;
    };
    /** Opt-in: the action link takes the focus when the toast shows (its region announces the message). */
    focusAction?: boolean;
    /** Opt-in: called when the person closes the toast with its button (e.g. to give the focus back). */
    onClose?: () => void;
}

export function internalHref(href: string): boolean {
    return (
        href.startsWith('/') &&
        !href.startsWith('//') &&
        !/[\\\u0000-\u0020]/.test(href)
    );
}

export const useToastStore = defineStore('toasts', () => {
    const notifications = ref<ToastNotification[]>([]);
    const renderKey = ref(0);
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const dismissed = new Set<string>();
    function dismiss(id: string, remember = true) {
        clearTimeout(timers.get(id));
        timers.delete(id);
        notifications.value = notifications.value.filter(
            (toast) => toast.id !== id,
        );
        if (remember && id.startsWith('ai-generation:')) dismissed.add(id);
    }
    function upsert(
        toast: Omit<ToastNotification, 'id'> & { id?: string },
        terminal = false,
    ) {
        const id = toast.id ?? crypto.randomUUID();
        if (dismissed.has(id) && !terminal) return;
        dismiss(id, false);
        if (terminal) dismissed.delete(id);
        const item = {
            ...toast,
            id,
            lifetimeKey: crypto.randomUUID(),
            timeout: toast.timeout === undefined ? 5000 : toast.timeout,
        };
        if (item.action && !internalHref(item.action.href)) delete item.action;
        notifications.value.push(item);
        if (item.timeout !== null)
            timers.set(
                id,
                setTimeout(() => dismiss(id, false), item.timeout),
            );
    }
    function clearPrefix(prefix: string) {
        // Context changes remove old content immediately, without a leave animation.
        renderKey.value++;
        notifications.value
            .filter((toast) => toast.id.startsWith(prefix))
            .forEach((toast) => dismiss(toast.id, false));
        for (const id of dismissed)
            if (id.startsWith(prefix)) dismissed.delete(id);
    }
    function cleanup() {
        timers.forEach(clearTimeout);
        timers.clear();
        notifications.value = [];
        dismissed.clear();
    }
    return { notifications, dismiss, upsert, clearPrefix, cleanup, renderKey };
});
